import { logger } from './logger';

// Email service utility for sending contact form emails
// This is a placeholder implementation - in production you would integrate with your email service

export interface ContactFormData {
    name: string;
    email: string;
    company: string;
    phone: string;
    message: string;
}

export interface EmailResponse {
    success: boolean;
    message: string;
    error?: string;
}

// Helper function to build API URL
const buildApiUrl = (endpoint: string): string => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
    // Remove trailing slash from backendUrl
    const baseUrl = backendUrl.replace(/\/+$/, '');
    // Remove leading slash from endpoint
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    // Check if baseUrl already contains /api
    if (baseUrl.endsWith('/api')) {
        return `${baseUrl}/${cleanEndpoint}`;
    }
    return `${baseUrl}/api/${cleanEndpoint}`;
};

// Send contact form email
export const sendContactEmail = async (formData: ContactFormData): Promise<EmailResponse> => {
    try {
        // Send request to backend API
        const response = await fetch(buildApiUrl('email/send-contact'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                name: formData.name,
                email: formData.email,
                company: formData.company,
                phone: formData.phone,
                message: formData.message
            })
        });
        
        if (!response.ok) {
            // Try to parse error response
            let errorMessage = 'Failed to send email';
            try {
                const errorResult = await response.json();
                errorMessage = errorResult.error || errorResult.message || errorMessage;
            } catch {
                // If response is not JSON, use status text
                errorMessage = response.statusText || `HTTP ${response.status}`;
            }
            
            return {
                success: false,
                message: 'Failed to send email',
                error: errorMessage
            };
        }
        
        const result = await response.json();
        
        if (result.success) {
            return {
                success: true,
                message: result.message || 'Email sent successfully!'
            };
        } else {
            return {
                success: false,
                message: result.error || 'Failed to send email',
                error: result.message
            };
        }
        
    } catch (error) {
        logger.error('Error sending email:', error);
        
        // Check if it's a network error
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                               errorMessage.includes('NetworkError');
        
        return {
            success: false,
            message: isNetworkError 
                ? 'Backend server is not available. Please check if the server is running.'
                : 'Failed to send email',
            error: errorMessage
        };
    }
};

// Generate HTML email template
const generateEmailHTML = (formData: ContactFormData): string => {
    return `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #1a365d;">New Contact Form Submission - Ultimate Plan Inquiry</h2>
            
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #2d3748; margin-top: 0;">Contact Information</h3>
                <p><strong>Name:</strong> ${formData.name}</p>
                <p><strong>Email:</strong> ${formData.email}</p>
                <p><strong>Company:</strong> ${formData.company || 'Not provided'}</p>
                <p><strong>Phone:</strong> ${formData.phone || 'Not provided'}</p>
            </div>
            
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #2d3748; margin-top: 0;">Message</h3>
                <p style="white-space: pre-wrap;">${formData.message}</p>
            </div>
            
            <div style="background: #e2e8f0; padding: 15px; border-radius: 8px; margin: 20px 0;">
                <p style="margin: 0; color: #64748b; font-size: 14px;">
                    This message was sent from the Quality Datum contact form for Ultimate Plan inquiries.
                </p>
            </div>
        </div>
    `;
};

// Generate plain text email template
const generateEmailText = (formData: ContactFormData): string => {
    return `
New Contact Form Submission - Ultimate Plan Inquiry

Contact Information:
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'Not provided'}
Phone: ${formData.phone || 'Not provided'}

Message:
${formData.message}

---
This message was sent from the Quality Datum contact form for Ultimate Plan inquiries.
    `.trim();
};

// Invitation-related email logic has been removed.

// Email validation utility
export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// Phone validation utility
export const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,}$/;
    return phoneRegex.test(phone);
};
